// src/components/rewards/StickerAlbum.js
//
// STICKER ALBUMS (step 5). Six albums — Space, Animals, Ocean, Music,
// Science, Garden — of 8 stickers each (6 common, 2 rare). Stickers come out
// of chests. Finish an album and claim its prize: coins plus a clothes
// colour that can't be bought anywhere (Nebula, Jungle, Ocean Wave…).
//
// Props: onClose()

import ModalPortal from "../common/ModalPortal";
import React, { useEffect, useState } from "react";
import { Coin } from "../coins/CoinCounter";
import { CATALOG_BY_ID } from "../avatar/avatarCatalog";
import { EXCLUSIVE_GRADIENTS } from "../avatar/avatarWear";
import { albumProgress, claimAlbum, REWARDS, useCollection } from "../../services/rewardsService";
import { albumCoins, dupCoins, useRewardSettings } from "../../services/rewardSettingsService";
import "./rewards.css";

const swatch = (id) => {
  const it = CATALOG_BY_ID[id];
  const g = it && EXCLUSIVE_GRADIENTS[it.pattern];
  return g ? `linear-gradient(135deg,${g.join(",")})` : it?.fill || "#ccc";
};

const StickerAlbum = ({ onClose }) => {
  const col = useCollection();
  const { settings } = useRewardSettings();
  const prizeCoins = albumCoins(settings);
  const [albumId, setAlbumId] = useState(REWARDS.albums[0].id);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const album = REWARDS.albums.find((a) => a.id === albumId) || REWARDS.albums[0];
  const found = albumProgress(album, col.stickers);
  const total = album.stickers.length;
  const done = found >= total;
  const claimed = Boolean(col.albums[album.id]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const claim = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const out = await claimAlbum(album.id);
      if (out?.coins) {
        window.dispatchEvent(new CustomEvent("sbl:coins-earned", { detail: { amount: out.coins, coins: out.balance, reason: "album" } }));
      }
      setMsg({ tone: "ok", text: `🎉 Album complete! You won ${prizeCoins} coins and the ${album.reward.itemName} colour — find it in Make my avatar → Clothes.` });
    } catch (e) {
      setMsg({ tone: "bad", text: e?.message || "Couldn't claim just now." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalPortal>
      <div className="sbl-rw-pop" role="dialog" aria-modal="true" aria-label="Sticker albums" onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}>
        <div className="sbl-album">
          <header className="sbl-rw-head is-album">
            <h2>📒 Sticker Albums</h2>
            <button type="button" className="sbl-rw-x is-inline" onClick={onClose} aria-label="Close">
              ✕
            </button>
          </header>

          <nav className="sbl-album-tabs" role="tablist">
            {REWARDS.albums.map((a) => {
              const n = albumProgress(a, col.stickers);
              return (
                <button key={a.id} type="button" role="tab" aria-selected={a.id === albumId} className={`${a.id === albumId ? "is-on" : ""} ${col.albums[a.id] ? "is-claimed" : ""}`} onClick={() => { setAlbumId(a.id); setMsg(null); }}>
                  <span aria-hidden="true">{a.icon}</span> {a.name}
                  <small>
                    {n}/{a.stickers.length}
                  </small>
                </button>
              );
            })}
          </nav>

          <div className="sbl-album-page">
            <div className="sbl-album-bar" aria-label={`${found} of ${total} stickers`}>
              <span style={{ width: `${(found / total) * 100}%` }} />
              <b>
                {found} / {total}
              </b>
            </div>

            <div className="sbl-album-grid">
              {album.stickers.map((s, i) => {
                const count = col.stickers[s.id] || 0;
                return (
                  <div key={s.id} className={`sbl-album-slot ${count ? "is-have" : ""} ${s.rare ? "is-rare" : ""}`}>
                    {count ? (
                      <span className="sbl-sticker" style={{ transform: `rotate(${((i * 37) % 13) - 6}deg)` }} aria-hidden="true">
                        {s.emoji}
                      </span>
                    ) : (
                      <span className="sbl-album-missing" aria-hidden="true">
                        ?
                      </span>
                    )}
                    <b>{count ? s.name : "???"}</b>
                    {s.rare && <span className="sbl-album-rare">★ Rare</span>}
                    {count > 1 && <span className="sbl-album-count">×{count}</span>}
                  </div>
                );
              })}
            </div>

            <div className={`sbl-album-prize ${done ? "is-ready" : ""}`}>
              <span className="sbl-album-swatch" style={{ background: swatch(album.reward.item) }} />
              <div>
                <b>Album prize:</b> <Coin size={16} /> {prizeCoins} coins + the <b>{album.reward.itemName}</b> clothes colour
                <br />
                <small>You can't buy it — only finish this album to win it!</small>
              </div>
              {claimed ? (
                <span className="sbl-album-claimed">✓ Won!</span>
              ) : (
                <button type="button" className="sbl-rw-btn" disabled={!done || busy} onClick={claim}>
                  {done ? (busy ? "…" : "Claim prize 🏆") : `${total - found} to go`}
                </button>
              )}
            </div>
            {msg && <p className={`sbl-rw-msg is-${msg.tone}`}>{msg.text}</p>}
            <p className="sbl-trail-note">Get stickers from chests: the daily trail (day 7), Treasure Quest, and your teacher. A sticker you already have turns into {dupCoins(settings)} coins.</p>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};

export default StickerAlbum;
